// Admin Panel -> ⚙ System -> 🔏 Data Privacy -> Retention Policy.
//
// This is a stated policy commitment, not an enforcement switch — saving a
// number here does not delete anything, now or later on a timer. See
// backend/routes/data-privacy.js's own comment on why an auto-purge job was
// deliberately not bundled into this.
import { useEffect, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { useRetentionPolicy, useSaveRetentionPolicy } from './hooks';

export default function RetentionPolicyTab() {
  const policy = useRetentionPolicy();
  const save = useSaveRetentionPolicy();

  const [years, setYears] = useState(8);
  const [notes, setNotes] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (policy.data && !dirty) {
      setYears(policy.data.retention_years);
      setNotes(policy.data.policy_notes || '');
    }
  }, [policy.data, dirty]);

  if (policy.isLoading) return <LoadingState label="Loading retention policy…" />;
  if (policy.isError) {
    return (
      <ErrorState
        message={policy.error instanceof ApiError ? policy.error.message : 'Could not load the retention policy'}
        onRetry={() => policy.refetch()}
      />
    );
  }

  const handleSave = () => {
    save.mutate(
      { retention_years: years, policy_notes: notes },
      { onSuccess: () => { setDirty(false); setSaved(true); setTimeout(() => setSaved(false), 2200); } }
    );
  };

  return (
    <div className="fade-in" style={{ maxWidth: 640 }}>
      <div className="mb-4 text-[13px] leading-relaxed text-ink-muted">
        How long this hospital commits to retaining patient records. This does not delete anything
        automatically — India's Clinical Establishments and medical-council rules require clinical
        records to be kept for a statutory period, so this number is a published commitment for staff
        and compliance reference, reviewed and (if ever built) enforced by a deliberate follow-up
        action, not a background timer.
      </div>

      <Card>
        <CardHeader>
          <CardTitle><CalendarClock size={15} /> Retention policy</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3.5">
            <FormField label="Retain records for (years)">
              <Input
                type="number" min={1}
                value={years}
                onChange={(e) => { setDirty(true); setYears(parseInt(e.target.value, 10) || 1); }}
              />
            </FormField>
          </div>
          <FormField label="Policy notes (optional, shown to staff)" className="mt-3.5">
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => { setDirty(true); setNotes(e.target.value); }}
              placeholder="e.g. Matches the hospital's own document-retention SOP, reviewed annually."
            />
          </FormField>
          {policy.data?.updated_by && (
            <div className="mt-2 text-[11.5px] text-ink-muted">
              Last changed by {policy.data.updated_by}
              {policy.data.updated_at ? ` on ${new Date(policy.data.updated_at).toLocaleString()}` : ''}.
            </div>
          )}
          <div className="mt-4">
            <Button onClick={handleSave} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : saved ? '✓ Saved' : 'Save policy'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
